import { useState, type MouseEvent } from 'react';
import { ChevronRight, MessageCircle, Plus } from 'lucide-react';
import { Button, Card, Typography } from '../ui';
import { Modal } from '../../Modal';
import { useTranslation } from '../../../lib/i18n';
import { useProductName } from '../../../lib/use-brand';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useLoadBi } from '../../../lib/use-load-bi';
import { useNeedsPhoneConnect, useOpenPhoneConnect } from '../../../lib/phone-connect';
import { WhatsAppMarkGlyph } from './BrandGlyphs';
import { useWhatsappGroups } from '../../settings/shared/settings-queries';
import { GroupFormPanel } from '../settings-whatsapp/GroupFormPanel';
import { GroupSettingsModal } from '../settings-whatsapp/GroupSettingsModal';
import { GroupsSection } from '../settings-whatsapp/GroupsSection';
import { useOpenWhatsappGroup } from '../settings-whatsapp/use-open-group';
import { WHATSAPP_DIRECT_URL, MAX_LIVE_GROUPS } from '../settings-whatsapp/constants';
import styles from './WhatsappChannelSection.module.css';

const CHANNELS_ROUTE = 'settings-connectors';

/**
 * The WhatsApp hub, desktop (Figma 13936-256717): three cards — **WhatsApp Groups** (opens a modal
 * listing the assistant-joined groups), **Create a new group** (the create modal), and **Chat with
 * {productName} on WhatsApp** (opens the direct chat). Reuses the shared group hooks + the
 * create/settings modals verbatim (live-group cap, invite links, rename, delete unchanged); only the
 * desktop framing is new.
 *
 * **The whole card is a hit target, and so is the Button inside it.** Clicking anywhere on a card runs
 * that card's own action — Groups and Create open their modals, Chat opens the direct thread — so the
 * cards take the full `interactive` state rather than `"hover"`: a card that acts must show the pressed
 * border, or the highlight is a promise it doesn't keep.
 *
 * The card stays a `div` with an `onClick` rather than `component="button"`, because each card keeps its
 * real Button (and Chat's is an `<a>`), and a button or anchor nested in a button is invalid HTML. That
 * leaves the inner control as the ONE thing assistive tech and the keyboard reach — which is why no
 * `role`/`tabIndex` is added here: the card click is a pointer convenience over an action that is
 * already reachable, not a second control to announce.
 *
 * ⚠️ Each card leads with the WhatsApp mark and the section title carries none (Figma 15378-167292) —
 * the opposite of what 14013-148742 asks for, and both frames are live in the file. The two halves
 * always move together: a mark in both places reads as the section saying "WhatsApp" four times.
 */
export function WhatsappChannelSection() {
  const { t } = useTranslation();
  const productName = useProductName();
  const { logPageClickBi } = useGenericBi();
  const { groups, loading, error } = useWhatsappGroups();
  useLoadBi('whatsapp-groups-data', { isLoading: loading, isError: error });
  const [groupsOpen, setGroupsOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const { group: settingsGroup, openGroup, closeGroup } = useOpenWhatsappGroup(groups);
  const atCap = groups.length >= MAX_LIVE_GROUPS;
  // Both CTAs need a number on file, so a caller without one is asked for it instead of being handed
  // the action: the direct thread would reach WhatsApp from a phone nothing can match back to them, and
  // the create is refused by the owner-phone precondition in `WaGroupOrchestrator` — after they have
  // named the group. Same rule either way, moved to the front of the flow.
  const needsPhone = useNeedsPhoneConnect();
  const openPhoneConnect = useOpenPhoneConnect();

  // The button is the same button whether or not it is gated, so it keeps its name and the OUTCOME is
  // what qualifies the event. Without that, an intercepted click still counts as a WhatsApp open and
  // the one number this work exists to move — how many phoneless callers were asked, and converted —
  // cannot be read back out.
  const clicked = (button_name: string) =>
    logPageClickBi({ page_name: CHANNELS_ROUTE, button_name }, needsPhone ? { componentName: 'connect-phone' } : undefined);

  const newGroup = () => {
    clicked('new_group');
    setGroupsOpen(false);
    if (needsPhone) {
      openPhoneConnect();
      return;
    }
    setCreating(true);
  };

  const openChat = () => clicked('open_whatsapp');

  const openGroups = () => {
    logPageClickBi({ page_name: CHANNELS_ROUTE, button_name: 'open_groups' });
    setGroupsOpen(true);
  };

  // The card-background route to the direct chat. Deliberately NOT a link, so the phone gate holds:
  // an `<a>` opens on cmd-click and middle-click without running any handler (the same reason the
  // gated Button below drops its href).
  const chatFromCard = () => {
    openChat();
    if (needsPhone) {
      openPhoneConnect();
      return;
    }
    window.open(WHATSAPP_DIRECT_URL, '_blank', 'noopener,noreferrer');
  };

  // Each card is a hit target AND holds its own control, so a click that landed on the control must not
  // ALSO run the card's handler — otherwise "Create" fires twice and "Chat" both follows the link and
  // opens a second window.
  //
  // The card ignores those clicks rather than each control stopping propagation, for two reasons. The
  // Chat card's control is a `<Button href>`, whose `onClick` is typed `(event?: ClickModifiers) => void`
  // — modifier fields, not an event — so it has no `stopPropagation` to call. And a guard here covers
  // any control added to these cards later, where a per-control `stopPropagation` is one more thing to
  // remember.
  const fromCardBackground = (run: () => void) => (e: MouseEvent<HTMLElement>) => {
    if ((e.target as HTMLElement).closest('a,button')) return;
    run();
  };

  return (
    <div className={styles.cards}>
      <Card variant="stone" size="sm" interactive className={`${styles.card} ${styles.clickable}`} onClick={fromCardBackground(openGroups)}>
        <span className={styles.cardGlyph} aria-hidden>
          <WhatsAppMarkGlyph size={20} />
        </span>
        <Typography variant="h4">{t('connectors.whatsapp.mobile.title')}</Typography>
        <div className={styles.action}>
          <Button
            variant="text"
            size="small"
            className={styles.availableBtn}
            endIcon={<ChevronRight size={16} aria-hidden className="dir-flip" />}
            onClick={openGroups}
          >
            <span className={styles.available}>{t('connectors.whatsapp.available', { count: groups.length })}</span>
          </Button>
        </div>
      </Card>

      {/* At the cap the action is refused, so the card must not offer it either — a disabled button
          inside a live card is the same broken promise the hover-only card was. */}
      <Card
        variant="stone"
        size="sm"
        interactive={atCap ? 'hover' : true}
        className={atCap ? styles.card : `${styles.card} ${styles.clickable}`}
        onClick={atCap ? undefined : fromCardBackground(newGroup)}
      >
        <span className={styles.cardGlyph} aria-hidden>
          <WhatsAppMarkGlyph size={20} />
        </span>
        <Typography variant="h4">{t('connectors.whatsapp.mobile.newGroup')}</Typography>
        <div className={styles.action}>
          <Button size="small" startIcon={<Plus size={16} aria-hidden />} disabled={atCap} onClick={newGroup}>
            {t('connectors.whatsapp.create')}
          </Button>
        </div>
      </Card>

      <Card variant="stone" size="sm" interactive className={`${styles.card} ${styles.clickable}`} onClick={fromCardBackground(chatFromCard)}>
        <span className={styles.cardGlyph} aria-hidden>
          <WhatsAppMarkGlyph size={20} />
        </span>
        <Typography variant="h4">{t('connectors.whatsapp.mobile.chat', { productName })}</Typography>
        <div className={styles.action}>
          {/* Gated, it must not be a link at all: an `<a>` still opens on cmd-click, middle-click and
              "open in new tab", any of which would walk straight past an onClick guard. */}
          {needsPhone ? (
            <Button
              variant="outlined"
              size="small"
              startIcon={<MessageCircle size={16} aria-hidden />}
              onClick={() => {
                openChat();
                openPhoneConnect();
              }}
            >
              {t('connectors.whatsapp.chatCta')}
            </Button>
          ) : (
            <Button
              variant="outlined"
              size="small"
              href={WHATSAPP_DIRECT_URL}
              external
              target="_blank"
              rel="noreferrer"
              startIcon={<MessageCircle size={16} aria-hidden />}
              onClick={openChat}
            >
              {t('connectors.whatsapp.chatCta')}
            </Button>
          )}
        </div>
      </Card>

      {groupsOpen && (
        <Modal
          open
          onClose={() => setGroupsOpen(false)}
          size="minimum"
          title={t('connectors.whatsapp.mobile.title')}
          labelledBy="wa-groups-title"
          headerSeparator
          footerSeparator
          maxHeightPx={650}
          scrollBody
          footer={
            <>
              <Button variant="outlined" onClick={() => setGroupsOpen(false)}>
                {t('common.close')}
              </Button>
              <Button startIcon={<Plus size={16} aria-hidden />} onClick={newGroup} disabled={atCap}>
                {t('settings.whatsapp.newGroup')}
              </Button>
            </>
          }
        >
          <GroupsSection
            groups={groups}
            loading={loading}
            error={error}
            selectedGroupId={null}
            onNewGroup={newGroup}
            onOpenGroup={(group) => {
              logPageClickBi({ page_name: CHANNELS_ROUTE, button_name: 'open_group' }, { componentName: 'open-group' });
              setGroupsOpen(false);
              openGroup(group);
            }}
          />
        </Modal>
      )}
      {creating && <GroupFormPanel onDone={() => setCreating(false)} />}
      {settingsGroup && <GroupSettingsModal group={settingsGroup} onClose={closeGroup} />}
    </div>
  );
}
